import React, { useState, useEffect } from 'react';
import { billingService } from '../../services/dataService';

const GSTIN_REGEX = /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/;

const DEFAULT_SETTINGS = {
  gst_enabled: false,
  gst_percent: 5,
  gstin: '',
  service_charge_enabled: false,
  service_charge_percent: 10,
  discount_enabled: false,
  discount_type: 'NONE',
  discount_value: 0,
  thermal_paper_width: 58,
  print_logo: false,
  print_tagline: false,
};

const inputStyle = {
  width: '100%', padding: '12px 15px',
  border: '2px solid #e5e7eb', borderRadius: '10px',
  fontSize: '1rem', outline: 'none',
  transition: 'border-color 0.2s', boxSizing: 'border-box',
};

const Toggle = ({ active, onToggle }) => (
  <div
    onClick={onToggle}
    style={{
      position: 'relative', display: 'inline-block',
      width: '52px', height: '28px',
      background: active ? 'linear-gradient(135deg, #FF4444, #FF6B00)' : '#d1d5db',
      borderRadius: '14px', cursor: 'pointer',
      transition: 'background 0.3s', flexShrink: 0,
    }}
  >
    <div style={{
      position: 'absolute', top: '3px',
      left: active ? '27px' : '3px',
      width: '22px', height: '22px',
      background: 'white', borderRadius: '50%',
      transition: 'left 0.3s', boxShadow: '0 1px 4px rgba(0,0,0,0.2)',
    }} />
  </div>
);

const Section = ({ title, children }) => (
  <div style={{
    background: 'rgba(255,255,255,0.95)', borderRadius: '16px',
    padding: '28px', boxShadow: '0 4px 20px rgba(0,0,0,0.06)',
    border: '1px solid rgba(0,0,0,0.06)',
  }}>
    <h2 style={{ margin: '0 0 20px 0', fontSize: '1.15rem', fontWeight: '700', color: '#333' }}>{title}</h2>
    {children}
  </div>
);

const ToggleRow = ({ label, description, active, onToggle }) => (
  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '20px' }}>
    <div>
      <div style={{ fontWeight: '600', color: '#333', marginBottom: '4px' }}>{label}</div>
      {description && <div style={{ fontSize: '0.82rem', color: '#888' }}>{description}</div>}
    </div>
    <Toggle active={active} onToggle={onToggle} />
  </div>
);

const BillingSettings = ({ restaurant }) => {
  const [form, setForm] = useState(DEFAULT_SETTINGS);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState('');
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (!restaurant?.id) return;
    loadSettings();
  }, [restaurant?.id]);

  const loadSettings = async () => {
    setLoading(true);
    const { data, error } = await billingService.getSettings(restaurant.id);
    if (error) {
      setErrors({ _load: 'Failed to load billing settings.' });
    } else if (data) {
      setForm({
        gst_enabled: data.gst_enabled ?? false,
        gst_percent: data.gst_percent ?? 5,
        gstin: data.gstin ?? '',
        service_charge_enabled: data.service_charge_enabled ?? false,
        service_charge_percent: data.service_charge_percent ?? 10,
        discount_enabled: data.discount_enabled ?? false,
        discount_type: data.discount_type ?? 'NONE',
        discount_value: data.discount_value ?? 0,
        thermal_paper_width: data.thermal_paper_width ?? 58,
        print_logo: data.print_logo ?? false,
        print_tagline: data.print_tagline ?? false,
      });
    }
    setLoading(false);
  };

  const set = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
    setSuccess('');
    setErrors(prev => ({ ...prev, [field]: undefined }));
  };

  const validate = () => {
    const e = {};
    if (form.gst_enabled) {
      const v = parseFloat(form.gst_percent);
      if (isNaN(v) || v <= 0 || v > 28) e.gst_percent = 'GST % must be between 0.01 and 28.';
      const g = form.gstin.trim().toUpperCase();
      if (!g || !GSTIN_REGEX.test(g)) e.gstin = 'Enter a valid 15-character GSTIN.';
    }
    if (form.service_charge_enabled) {
      const v = parseFloat(form.service_charge_percent);
      if (isNaN(v) || v <= 0 || v > 20) e.service_charge_percent = 'Service charge % must be between 0.01 and 20.';
    }
    if (form.discount_enabled) {
      if (form.discount_type === 'NONE') {
        e.discount_type = 'Select a discount type.';
      } else if (form.discount_type === 'PERCENT') {
        const v = parseFloat(form.discount_value);
        if (isNaN(v) || v <= 0 || v > 100) e.discount_value = 'Discount % must be between 0.01 and 100.';
      } else if (form.discount_type === 'FIXED') {
        const v = parseFloat(form.discount_value);
        if (isNaN(v) || v < 0) e.discount_value = 'Discount amount must be ≥ 0.';
      }
    }
    return e;
  };

  const handleSave = async () => {
    const e = validate();
    if (Object.keys(e).length > 0) { setErrors(e); return; }
    setSaving(true); setSuccess(''); setErrors({});

    const payload = {
      gst_enabled: form.gst_enabled,
      gst_percent: form.gst_enabled ? parseFloat(form.gst_percent) : 0,
      gstin: form.gst_enabled ? form.gstin.trim().toUpperCase() : null,
      service_charge_enabled: form.service_charge_enabled,
      service_charge_percent: form.service_charge_enabled ? parseFloat(form.service_charge_percent) : 0,
      discount_enabled: form.discount_enabled,
      discount_type: form.discount_enabled ? form.discount_type : 'NONE',
      discount_value: form.discount_enabled ? parseFloat(form.discount_value) || 0 : 0,
      pricing_mode: 'EXCLUSIVE',
      thermal_paper_width: form.thermal_paper_width,
      print_logo: form.print_logo,
      print_tagline: form.print_tagline,
    };

    const { error } = await billingService.saveSettings(restaurant.id, payload);
    setSaving(false);
    if (error) setErrors({ _save: 'Failed to save. ' + (error.message || '') });
    else setSuccess('Billing settings saved successfully.');
  };

  if (loading) return <div style={{ padding: '40px', textAlign: 'center', color: '#666' }}>Loading billing settings...</div>;

  return (
    <div style={{ maxWidth: '1400px', margin: '0 auto', padding: '30px 20px' }}>
      <div style={{ marginBottom: '28px' }}>
        <h1 style={{ margin: '0 0 6px 0', fontSize: '2rem', fontWeight: '700', color: '#333' }}>Billing Settings</h1>
        <p style={{ margin: '0 0 10px 0', color: '#666' }}>Configure taxes, charges, and default discount applied to counter orders.</p>
      </div>

      {(success || errors._load || errors._save) && (
        <div style={{
          background: success ? '#d4edda' : '#fee2e2',
          border: `1px solid ${success ? '#c3e6cb' : '#fecaca'}`,
          borderRadius: '10px', padding: '14px 18px', marginBottom: '24px',
          color: success ? '#155724' : '#dc2626', fontSize: '0.95rem',
        }}>
          {success || errors._load || errors._save}
        </div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>

        {/* GST */}
        <Section title="GST / Tax">
          <ToggleRow
            label="Enable GST"
            description="Apply GST on all counter orders"
            active={form.gst_enabled}
            onToggle={() => set('gst_enabled', !form.gst_enabled)}
          />
          {form.gst_enabled && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              <div>
                <label style={{ display: 'block', fontWeight: '600', color: '#333', marginBottom: '8px', fontSize: '0.9rem' }}>GST Rate (%) <span style={{ color: '#dc2626' }}>*</span></label>
                <input
                  type="number" min="0.01" max="28" step="0.5" value={form.gst_percent}
                  onChange={e => set('gst_percent', e.target.value)}
                  style={{ ...inputStyle, borderColor: errors.gst_percent ? '#dc2626' : '#e5e7eb' }}
                  onFocus={e => e.target.style.borderColor = '#FF6B00'}
                  onBlur={e => e.target.style.borderColor = errors.gst_percent ? '#dc2626' : '#e5e7eb'}
                />
                {errors.gst_percent && <p style={{ margin: '5px 0 0', color: '#dc2626', fontSize: '0.82rem' }}>{errors.gst_percent}</p>}
                <p style={{ margin: '6px 0 0', color: '#888', fontSize: '0.82rem' }}>Mode: Exclusive (added on top of item price). Max 28%.</p>
              </div>
              <div>
                <label style={{ display: 'block', fontWeight: '600', color: '#333', marginBottom: '8px', fontSize: '0.9rem' }}>GSTIN <span style={{ color: '#dc2626' }}>*</span></label>
                <input
                  type="text" placeholder="e.g. 29ABCDE1234F1Z5" maxLength={15}
                  value={form.gstin}
                  onChange={e => set('gstin', e.target.value.trim().toUpperCase())}
                  style={{ ...inputStyle, letterSpacing: '0.05em', borderColor: errors.gstin ? '#dc2626' : '#e5e7eb' }}
                  onFocus={e => e.target.style.borderColor = '#FF6B00'}
                  onBlur={e => e.target.style.borderColor = errors.gstin ? '#dc2626' : '#e5e7eb'}
                />
                {errors.gstin
                  ? <p style={{ margin: '5px 0 0', color: '#dc2626', fontSize: '0.82rem' }}>{errors.gstin}</p>
                  : <p style={{ margin: '6px 0 0', color: '#888', fontSize: '0.82rem' }}>15-character GST Identification Number. Required when GST is enabled.</p>
                }
              </div>
            </div>
          )}
        </Section>

        {/* Service Charge */}
        <Section title="Service Charge">
          <ToggleRow
            label="Enable Service Charge"
            description="Add a service charge to counter orders"
            active={form.service_charge_enabled}
            onToggle={() => set('service_charge_enabled', !form.service_charge_enabled)}
          />
          {form.service_charge_enabled && (
            <div>
              <label style={{ display: 'block', fontWeight: '600', color: '#333', marginBottom: '8px', fontSize: '0.9rem' }}>Service Charge Rate (%) <span style={{ color: '#dc2626' }}>*</span></label>
              <input
                type="number" min="0.01" max="20" step="0.5" value={form.service_charge_percent}
                onChange={e => set('service_charge_percent', e.target.value)}
                style={{ ...inputStyle, borderColor: errors.service_charge_percent ? '#dc2626' : '#e5e7eb' }}
                onFocus={e => e.target.style.borderColor = '#FF6B00'}
                onBlur={e => e.target.style.borderColor = errors.service_charge_percent ? '#dc2626' : '#e5e7eb'}
              />
              {errors.service_charge_percent && <p style={{ margin: '5px 0 0', color: '#dc2626', fontSize: '0.82rem' }}>{errors.service_charge_percent}</p>}
              <p style={{ margin: '6px 0 0', color: '#888', fontSize: '0.82rem' }}>Max 20%.</p>
            </div>
          )}
        </Section>

        {/* Default Discount */}
        <Section title="Default Discount">
          <ToggleRow
            label="Enable Default Discount"
            description="Pre-fill a discount on every bill (manager can adjust per order)"
            active={form.discount_enabled}
            onToggle={() => {
              const next = !form.discount_enabled;
              setForm(prev => ({ ...prev, discount_enabled: next, discount_type: next ? (prev.discount_type === 'NONE' ? 'PERCENT' : prev.discount_type) : 'NONE', discount_value: next ? prev.discount_value : 0 }));
              setSuccess(''); setErrors({});
            }}
          />
          {form.discount_enabled && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
              <div>
                <label style={{ display: 'block', fontWeight: '600', color: '#333', marginBottom: '8px', fontSize: '0.9rem' }}>Discount Type <span style={{ color: '#dc2626' }}>*</span></label>
                <div style={{ display: 'flex', gap: '10px' }}>
                  {[{ id: 'PERCENT', label: '% Percent' }, { id: 'FIXED', label: '₹ Fixed' }].map(t => (
                    <button
                      key={t.id}
                      onClick={() => set('discount_type', t.id)}
                      style={{
                        flex: 1, padding: '10px', borderRadius: '10px', fontWeight: '600', fontSize: '0.9rem', cursor: 'pointer',
                        background: form.discount_type === t.id ? 'linear-gradient(135deg, #FF4444, #FF6B00)' : 'transparent',
                        color: form.discount_type === t.id ? 'white' : '#555',
                        border: form.discount_type === t.id ? 'none' : '2px solid #e5e7eb',
                      }}
                    >{t.label}</button>
                  ))}
                </div>
                {errors.discount_type && <p style={{ margin: '5px 0 0', color: '#dc2626', fontSize: '0.82rem' }}>{errors.discount_type}</p>}
              </div>
              <div>
                <label style={{ display: 'block', fontWeight: '600', color: '#333', marginBottom: '8px', fontSize: '0.9rem' }}>
                  {form.discount_type === 'PERCENT' ? 'Discount %' : 'Discount Amount (₹)'} <span style={{ color: '#dc2626' }}>*</span>
                </label>
                <input
                  type="number" min="0" step={form.discount_type === 'PERCENT' ? '0.5' : '1'} value={form.discount_value}
                  max={form.discount_type === 'PERCENT' ? 100 : undefined}
                  onChange={e => set('discount_value', e.target.value)}
                  style={{ ...inputStyle, borderColor: errors.discount_value ? '#dc2626' : '#e5e7eb' }}
                  onFocus={e => e.target.style.borderColor = '#FF6B00'}
                  onBlur={e => e.target.style.borderColor = errors.discount_value ? '#dc2626' : '#e5e7eb'}
                />
                {errors.discount_value && <p style={{ margin: '5px 0 0', color: '#dc2626', fontSize: '0.82rem' }}>{errors.discount_value}</p>}
              </div>
            </div>
          )}
        </Section>

        {/* Thermal Paper */}
        <Section title="Thermal Printer">
          <div style={{ marginBottom: '8px', fontWeight: '600', color: '#333', fontSize: '0.9rem' }}>Paper Width</div>
          <div style={{ display: 'flex', gap: '10px' }}>
            {[{ val: 80, label: '80 mm' }, { val: 58, label: '58 mm' }].map(opt => (
              <button
                key={opt.val}
                onClick={() => set('thermal_paper_width', opt.val)}
                style={{
                  flex: 1, padding: '10px', borderRadius: '10px', fontWeight: '600', fontSize: '0.9rem', cursor: 'pointer',
                  background: form.thermal_paper_width === opt.val ? 'linear-gradient(135deg, #FF4444, #FF6B00)' : 'transparent',
                  color: form.thermal_paper_width === opt.val ? 'white' : '#555',
                  border: form.thermal_paper_width === opt.val ? 'none' : '2px solid #e5e7eb',
                }}
              >{opt.label}</button>
            ))}
          </div>
          <p style={{ margin: '10px 0 0', color: '#888', fontSize: '0.82rem' }}>
            Match this to your printer's paper roll size. Affects the print layout only.
          </p>
        </Section>

        {/* Receipt Branding */}
        <Section title="Receipt Branding">
          <ToggleRow
            label="Print Logo"
            description="Show restaurant logo at the top of printed receipts"
            active={form.print_logo}
            onToggle={() => set('print_logo', !form.print_logo)}
          />
          <ToggleRow
            label="Print Tagline"
            description="Show restaurant tagline on printed receipts"
            active={form.print_tagline}
            onToggle={() => set('print_tagline', !form.print_tagline)}
          />
          <p style={{ margin: '4px 0 0', color: '#888', fontSize: '0.82rem' }}>
            Logo and tagline are set in the restaurant's Profile tab.
          </p>
        </Section>

      </div>

      <div style={{ marginTop: '28px', display: 'flex', justifyContent: 'flex-end' }}>
        <button
          onClick={handleSave}
          disabled={saving}
          style={{
            background: saving ? '#ccc' : 'linear-gradient(135deg, #FF4444, #FF6B00)',
            color: 'white', border: 'none', borderRadius: '12px',
            padding: '14px 32px', fontSize: '1rem', fontWeight: '700',
            cursor: saving ? 'not-allowed' : 'pointer',
            boxShadow: saving ? 'none' : '0 4px 15px rgba(255,68,68,0.3)',
          }}
        >
          {saving ? 'Saving...' : 'Save Billing Settings'}
        </button>
      </div>
    </div>
  );
};

export default BillingSettings;
